// biome-ignore-all lint/suspicious/noArrayIndexKey: plan cells render in a stable order and are not reordered.
// =============================================================================
// ProductSyncModal — the Unleashed Products sync, in a draggable modal
// =============================================================================
// Launched from the Products screen ("Sync from Unleashed" button). Pulls the
// product catalogue from Unleashed via the `unleashed-fetch` Edge Function,
// diffs it against the local `unleashed_products` catalogue (planProductSync),
// and shows a preview-and-tick list; Apply writes the selected create/update/
// obsolete changes and refreshes the underlying list (onApplied). This is the
// former "Products" section of SyncUtilitiesAdmin, moved here (John, 1 Aug 2026).
// =============================================================================
import React from "react";
import { useSession } from "../../app/SessionContext.jsx";
import { createProduct, listProducts, updateProductById } from "../../data/unleashedProducts";
import { planProductSync } from "../../services/unleashedProductSync";
import { logAudit } from "../../utils/auditLog";
import { getSupabaseClient } from "../../utils/supabase";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

const cellTh = {
  textAlign: "left",
  padding: "6px 10px",
  fontSize: 11,
  fontWeight: 700,
  color: "#607D79",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  borderBottom: "1px solid #E0EDEB",
};
const cellTd = { padding: "6px 10px", fontSize: 13, borderBottom: "1px solid #F0F0F0" };

function Row({ checked, onToggle, cells, label }) {
  return (
    <tr>
      <td style={cellTd}>
        <input type="checkbox" checked={checked} onChange={onToggle} aria-label={label} />
      </td>
      {cells.map(function (c, i) {
        return (
          <td key={i} style={cellTd}>
            {c}
          </td>
        );
      })}
    </tr>
  );
}

function PlanTable({ title, head, rows, selected, onToggle }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#00695C", marginBottom: 4 }}>
        {title}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...cellTh, width: 28 }} />
            {head.map(function (hh) {
              return (
                <th key={hh} style={cellTh}>
                  {hh}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map(function (r) {
            return (
              <Row
                key={r.key}
                checked={selected.has(r.key)}
                onToggle={function () {
                  onToggle(r.key);
                }}
                cells={r.cells}
                label={r.label}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ProductSyncModal({ onClose, onApplied }) {
  const { user } = useSession();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [plan, setPlan] = React.useState(null);
  const [summary, setSummary] = React.useState("");
  // Selection keys: "create:<code>", "update:<id>", "obsolete:<id>". All on by default.
  const [selected, setSelected] = React.useState(function () {
    return new Set();
  });

  const toggle = React.useCallback(function (key) {
    setSelected(function (prev) {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  function clearPreview() {
    setPlan(null);
    setSelected(new Set());
    setSummary("");
    setError("");
  }

  async function runSync() {
    setBusy(true);
    setError("");
    setSummary("");
    setPlan(null);
    try {
      const supa = getSupabaseClient();
      if (!supa) {
        setError("Could not reach the server.");
        return;
      }
      const { data, error: invErr } = await supa.functions.invoke("unleashed-fetch", {
        body: { resource: "Products" },
      });
      if (invErr) {
        setError(`Fetch failed: ${invErr.message}`);
        return;
      }
      if (!data || data.ok === false) {
        const st = data?.status ? ` (${data.status})` : "";
        setError(`Unleashed error: ${data?.error || "unknown"}${st}`);
        return;
      }
      const current = (await listProducts({ limit: 5000 })) || [];
      const p = planProductSync(data.items || [], current);
      // Default every proposed change to selected.
      const keys = new Set();
      for (const c of p.toCreate) keys.add(`create:${c.product_code}`);
      for (const u of p.toUpdate) keys.add(`update:${u.id}`);
      for (const o of p.toObsolete) keys.add(`obsolete:${o.id}`);
      setSelected(keys);
      setPlan(p);
    } catch (e) {
      setError(`Sync failed: ${e?.message || "error"}`);
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    if (!plan) return;
    setBusy(true);
    setError("");
    let created = 0;
    let updated = 0;
    let obsoleted = 0;
    try {
      for (const c of plan.toCreate) {
        if (!selected.has(`create:${c.product_code}`)) continue;
        await createProduct({
          product_code: c.product_code,
          product_description: c.product_description,
          product_group: c.product_group,
          base_pack: c.base_pack,
          base_unit: c.base_unit,
          deactivated: false,
        });
        created += 1;
      }
      for (const u of plan.toUpdate) {
        if (!selected.has(`update:${u.id}`)) continue;
        await updateProductById(u.id, {
          product_description: u.next.product_description,
          product_group: u.next.product_group,
          base_pack: u.next.base_pack,
          base_unit: u.next.base_unit,
          deactivated: u.next.obsolete,
        });
        updated += 1;
      }
      for (const o of plan.toObsolete) {
        if (!selected.has(`obsolete:${o.id}`)) continue;
        await updateProductById(o.id, { deactivated: true });
        obsoleted += 1;
      }
      logAudit("unleashed_products_synced", { created, updated, obsoleted, by: user?.shelterName });
      setSummary(`Applied — ${created} created, ${updated} updated, ${obsoleted} marked obsolete.`);
      setPlan(null);
      if (onApplied) onApplied();
    } catch (e) {
      setError(`Apply failed: ${e?.message || "error"}`);
    } finally {
      setBusy(false);
    }
  }

  const changeCount = plan
    ? plan.toCreate.length + plan.toUpdate.length + plan.toObsolete.length
    : 0;

  return (
    <ModalBackdrop
      onClose={onClose}
      portal
      draggable
      dragTitle="Sync Products from Unleashed"
      contentStyle={{ maxWidth: 800, width: "92vw", background: "#fff", borderRadius: 10 }}
    >
      <div style={{ padding: 20, maxHeight: "72vh", overflow: "auto" }}>
        <p style={{ margin: "0 0 14px", fontSize: 13, color: "#607D79" }}>
          Pull the product catalogue from Unleashed and reconcile it into the local Products list.
          Nothing is changed until you review the results and click Apply.
        </p>

        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <Btn onClick={runSync} disabled={busy}>
            {busy ? "Working…" : "Sync Products FROM Unleashed TO Portal"}
          </Btn>
          {summary && (
            <span style={{ fontSize: 13, fontWeight: 600, color: "#2E7D32" }}>{summary}</span>
          )}
          {error && (
            <span style={{ fontSize: 13, fontWeight: 600, color: "#C62828" }}>{error}</span>
          )}
        </div>

        {plan && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 13, color: "#455A64", marginBottom: 12 }}>
              <strong>{plan.toCreate.length}</strong> new, <strong>{plan.toUpdate.length}</strong>{" "}
              changed, <strong>{plan.toObsolete.length}</strong> now obsolete,{" "}
              <strong>{plan.unchangedCount}</strong> unchanged
              {plan.skippedNewObsolete > 0
                ? ` (${plan.skippedNewObsolete} new-but-obsolete skipped)`
                : ""}
              .
            </div>

            {plan.toCreate.length > 0 && (
              <PlanTable
                title="New products"
                head={["Code", "Description", "Group"]}
                rows={plan.toCreate.map(function (c) {
                  return {
                    key: `create:${c.product_code}`,
                    label: `Create ${c.product_code}`,
                    cells: [c.product_code, c.product_description || "—", c.product_group || "—"],
                  };
                })}
                selected={selected}
                onToggle={toggle}
              />
            )}

            {plan.toUpdate.length > 0 && (
              <PlanTable
                title="Changed products"
                head={["Code", "Changes"]}
                rows={plan.toUpdate.map(function (u) {
                  return {
                    key: `update:${u.id}`,
                    label: `Update ${u.code}`,
                    cells: [
                      u.code,
                      u.changes
                        .map(function (ch) {
                          return `${ch.field}: ${ch.from ?? "—"} → ${ch.to ?? "—"}`;
                        })
                        .join("; "),
                    ],
                  };
                })}
                selected={selected}
                onToggle={toggle}
              />
            )}

            {plan.toObsolete.length > 0 && (
              <PlanTable
                title="Now obsolete in Unleashed"
                head={["Code", "Description"]}
                rows={plan.toObsolete.map(function (o) {
                  return {
                    key: `obsolete:${o.id}`,
                    label: `Mark ${o.code} obsolete`,
                    cells: [o.code, o.description || "—"],
                  };
                })}
                selected={selected}
                onToggle={toggle}
              />
            )}

            {changeCount > 0 ? (
              <div style={{ marginTop: 14, display: "flex", gap: 12, alignItems: "center" }}>
                <Btn onClick={apply} disabled={busy}>
                  {busy ? "Applying…" : "Apply selected changes"}
                </Btn>
                <button
                  type="button"
                  onClick={clearPreview}
                  disabled={busy}
                  style={{
                    background: "white",
                    color: "#607D79",
                    border: "1px solid #CFD8DC",
                    borderRadius: 6,
                    padding: "8px 16px",
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: "inherit",
                    cursor: busy ? "not-allowed" : "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "#607D79" }}>
                  Everything is already in sync — nothing to apply.
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </ModalBackdrop>
  );
}
